/**
 * Маппинг сохранённых/расшаренных результатов расчёта в формы компонентов.
 *
 * Общий код для DashboardView и EmbedView — ранее дублировался байт-в-байт.
 */
import { FISCAL_MONTH_KEYS } from './fiscalMonths';

/** Convert old sales_plan format (objects) → new SalesPlanData (arrays). Pass-through if already new. */
export function mapSalesPlan(raw: any): any {
  if (!raw) return undefined;
  // Already new format — tacos is an array
  if (Array.isArray(raw.tacos)) return raw;

  const result: any = {};

  // tacos: {m1: 20, m2: 20, ..., target: 20} → [{month:1, tacos:20}, ...]
  if (raw.tacos && typeof raw.tacos === 'object') {
    const entries: {month: number; tacos: number}[] = [];
    for (const [k, v] of Object.entries(raw.tacos)) {
      if (k === 'target') continue;
      const m = parseInt(k.replace('m', ''), 10);
      if (!isNaN(m)) entries.push({ month: m, tacos: Number(v) || 0 });
    }
    entries.sort((a, b) => a.month - b.month);
    result.tacos = entries;
    result.targetTacos = Number(raw.tacos.target) || 0;
  }

  // annualGrowth: {"2025": 0, ...} → salesGrowth: [{year, annualGrowth, monthlyGrowth}, ...]
  if (raw.annualGrowth && typeof raw.annualGrowth === 'object' && !Array.isArray(raw.annualGrowth)) {
    result.salesGrowth = Object.entries(raw.annualGrowth).map(([y, v]) => {
      const annual = Number(v) || 0;
      const mg = (Math.pow(1 + annual / 100, 1 / 12) - 1) * 100;
      return { year: Number(y), annualGrowth: annual, monthlyGrowth: +mg.toFixed(2) };
    }).sort((a, b) => a.year - b.year);
  }

  // monthlyPlan: {july: -37, ...} → monthlyGrowth2025: [{month:1, monthName:'july', growth:-37}, ...]
  // monthName в payload — канонический английский ключ ('july', 'august', ...),
  // чтобы XLSX-экспорт и шеринг не зависели от UI-локали.
  if (raw.monthlyPlan && typeof raw.monthlyPlan === 'object') {
    result.monthlyGrowth2025 = FISCAL_MONTH_KEYS.map((key, i) => ({
      month: i + 1,
      monthName: key,
      growth: Number((raw.monthlyPlan as any)[key]) || 0,
    }));
  }

  return result;
}

export function mapCalcData(raw: Record<string, unknown> | undefined) {
  if (!raw) return undefined;
  return {
    purchasePrice: raw.purchase_price as number,
    shippingCostPerKg: raw.shipping_cost_per_kg as number,
    unitWeightKg: raw.unit_weight_kg as number,
    storageFee: (raw.storage_fee ?? raw.storage_fee_per_unit) as number,
    sellingPrice: raw.selling_price as number,
    referralFee: raw.referral_fee as number,
    fbaFee: raw.fba_fee as number,
    otherFees: raw.other_fees as number,
    ppcCost: raw.ppc_cost as number,
    conversionRate: raw.conversion_rate as number,
    ppcSalesPercentage: raw.ppc_sales_percentage as number,
    returnRate: raw.return_rate as number,
    resellableReturns: raw.resellable_returns as number,
    monthlySales: raw.monthly_sales as number,
    batchQuantity: raw.batch_quantity as number,
    couponPromoEnabled: raw.coupon_promo_enabled as boolean ?? true,
    asin: (raw.parent_asin as string) || undefined,
    competitorAsins: Array.isArray(raw.competitors)
      ? (raw.competitors as { asin: string }[]).map((c) => c.asin)
      : undefined,
  };
}
